import React, { useCallback, useMemo } from "react";
import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import {
    buildOutcomeFilterOptions,
    resolveOutcomeSelection,
} from "@components/activityTimeline/activityOutcomeFilter.lib";
import { Picker } from "@design-system/Picker/Picker.lib";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import type { ActivityFilterRows } from "@components/activityTimeline/filters/activityFilters.types";
import type { HistoryChangeFilters } from "@hooks/view/useHistoryPageFiltersCache";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { CLEARED_OBJECT_FIELD_FILTERS } from "../utils/historyFilterClears";
import {
    buildActivityFilters,
    isActivityValueEnabled,
} from "./historyActivityFilters";
import {
    CATEGORY_OBJECT_FIELD_VALUE,
    HISTORY_OBJECT_FIELD_OPTIONS,
    HistoryObjectFieldOption,
} from "./historyObjectFieldFilterOptions";

interface Props {
    filters: HistoryChangeFilters;
    onChange: (filters: HistoryChangeFilters) => void;
    /** Outcomes listed above the objects, for pages whose list filters by `filters.outcome`; none lists objects only. */
    outcomes?: readonly ActivityOutcomeKind[];
    /** The objects listed; workspace History's by default. */
    options?: readonly HistoryObjectFieldOption[];
}

/**
 * All activity: object and field picks, plus any `outcomes` the page offers,
 * one at a time; picking an outcome clears the object picks and picking an
 * object clears the outcome.
 */
export function useObjectFieldFilterRows({
    filters,
    onChange,
    outcomes,
    options = HISTORY_OBJECT_FIELD_OPTIONS,
}: Props): ActivityFilterRows<string> {
    const { enabled: isGlFirst } = useIsGlFirst();
    const { schedules: isSchedulesEnabled } = useTypedFlags();
    const outcome =
        filters.outcome && outcomes?.includes(filters.outcome)
            ? filters.outcome
            : undefined;

    const availableOptions = useMemo<HistoryObjectFieldOption[]>(
        () =>
            options.filter((option) => {
                if (isGlFirst && option.value === CATEGORY_OBJECT_FIELD_VALUE) {
                    return false;
                }
                return isActivityValueEnabled(option.value, {
                    isSchedulesEnabled,
                });
            }),
        [options, isGlFirst, isSchedulesEnabled],
    );
    const optionItems = useMemo(
        () =>
            availableOptions.map((option) => ({
                value: option.value,
                label: option.label,
            })),
        [availableOptions],
    );
    const selectedActivityValues = useMemo(
        () => filters.activityValues ?? [],
        [filters.activityValues],
    );

    const selectedActivities = useMemo(
        () =>
            availableOptions.filter((option) =>
                selectedActivityValues.includes(option.value),
            ),
        [availableOptions, selectedActivityValues],
    );
    const selectedObjectValues = useMemo(
        () => selectedActivities.map((option) => option.value),
        [selectedActivities],
    );
    const { outcomeItems, sections, labelsByValue, selectedValues } = useMemo(
        () =>
            buildOutcomeFilterOptions({
                outcomes: outcomes ?? [],
                outcome,
                objectSection: {
                    key: "objects",
                    title: "Objects",
                    items: optionItems,
                },
                selectedObjectValues,
            }),
        [outcomes, outcome, optionItems, selectedObjectValues],
    );

    const applySelection = useCallback(
        (values: string[]) => {
            const selection = resolveOutcomeSelection({
                values,
                outcome,
                selectedObjectValues,
            });
            const nextActivities = availableOptions.filter((option) =>
                selection.objectValues.includes(option.value),
            );
            const nextValues = nextActivities.map((a) => a.value);
            onChange({
                activityValues: nextValues.length > 0 ? nextValues : undefined,
                activityFilters: buildActivityFilters(
                    nextActivities,
                    filters.changeTypes,
                ),
                resourceTypes: undefined,
                fields: undefined,
                outcome: selection.outcome,
            });
        },
        [
            availableOptions,
            filters.changeTypes,
            onChange,
            outcome,
            selectedObjectValues,
        ],
    );

    const clearFilter = useCallback(() => {
        onChange({ ...CLEARED_OBJECT_FIELD_FILTERS, outcome: undefined });
    }, [onChange]);

    const selectedCount = selectedValues.length;

    let chipLabel: string | undefined;
    if (selectedCount === 1) {
        chipLabel = labelsByValue.get(selectedValues[0]);
    } else if (selectedCount > 1) {
        chipLabel = `${selectedCount} activities`;
    }

    return {
        list: outcomeItems.length > 0 ? { sections } : { items: optionItems },
        value: selectedValues,
        onValueChange: applySelection,
        onClear: clearFilter,
        chipLabel,
    };
}

export const ObjectFieldFilterCombobox: React.FC<Props> = (props) => {
    const rows = useObjectFieldFilterRows(props);
    const selectedCount = rows.value.length;

    return (
        <Picker.Select
            {...rows.list}
            multiple
            searchable
            searchPlaceholder="Search activity..."
            value={rows.value}
            onValueChange={rows.onValueChange}
            placeholder="All activity"
            renderValue={() => rows.chipLabel}
        >
            {selectedCount > 0 && (
                <Picker.Footer>
                    <FilterActionsFooter
                        deselectFunction={rows.onClear}
                        selectedCount={selectedCount}
                        hideBorderAndPadding
                    />
                </Picker.Footer>
            )}
        </Picker.Select>
    );
};
